import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const LanguageSwitcherContainer = styled.div`
    position: ${props => props.inline ? 'relative' : 'fixed'};
    top: ${props => props.inline ? 'auto' : '20px'};
    right: ${props => props.inline ? 'auto' : '20px'};
    z-index: 1000;
`;

const LanguageButton = styled.button`
    background: ${props => props.inline ? 'rgba(244, 212, 163, 0.1)' : 'rgba(255, 255, 255, 0.2)'};
    backdrop-filter: blur(10px);
    border: 2px solid ${props => props.inline ? 'rgba(244, 212, 163, 0.3)' : 'rgba(255, 255, 255, 0.3)'};
    color: ${props => props.inline ? '#f4d4a3' : 'white'};
    padding: ${props => props.inline ? '8px 16px' : '10px 20px'};
    border-radius: 25px;
    font-size: ${props => props.inline ? '0.85rem' : '0.9rem'};
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        background: ${props => props.inline ? 'rgba(244, 212, 163, 0.2)' : 'rgba(255, 255, 255, 0.3)'};
        transform: translateY(-2px);
        border-color: ${props => props.inline ? 'rgba(244, 212, 163, 0.5)' : 'rgba(255, 255, 255, 0.5)'};
    }

    @media (max-width: 768px) {
        padding: ${props => props.inline ? '6px 12px' : '10px 20px'};
        font-size: ${props => props.inline ? '0.75rem' : '0.9rem'};
    }
`;

const LanguageDropdown = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 10px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
    overflow: hidden;
    min-width: 180px;
    animation: fadeInDown 0.3s ease;
    border: 1px solid rgba(255, 255, 255, 0.3);

    @keyframes fadeInDown {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const LanguageOption = styled.button`
    width: 100%;
    padding: 12px 20px;
    border: none;
    background: transparent;
    color: #333;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 10px;

    &:hover {
        background: rgba(102, 126, 234, 0.1);
    }

    &:active {
        background: rgba(102, 126, 234, 0.2);
    }

    ${props => props.active && `
        background: rgba(102, 126, 234, 0.15);
        font-weight: 600;
        color: #667eea;
    `}
`;

const DialogOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(5px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.3s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const Dialog = styled.div`
    background: white;
    border-radius: 20px;
    padding: 30px;
    max-width: 400px;
    width: 90%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    animation: scaleIn 0.3s ease;

    @keyframes scaleIn {
        from {
            transform: scale(0.9);
            opacity: 0;
        }
        to {
            transform: scale(1);
            opacity: 1;
        }
    }
`;

const DialogTitle = styled.h3`
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: #333;
    text-align: center;
`;

const DialogMessage = styled.p`
    font-size: 1rem;
    line-height: 1.8;
    color: #666;
    margin-bottom: 25px;
    text-align: center;
`;

const DialogButton = styled.button`
    width: 100%;
    padding: 12px 24px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
    }

    &:active {
        transform: translateY(0);
    }
`;

const LanguageSwitcher = ({ inline = false }) => {
    const { i18n, t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [showKoreanDialog, setShowKoreanDialog] = useState(false);

    const languages = [
        { code: 'zh-TW', label: t('language.zhTW'), flag: '🇹🇼' },
        { code: 'ja', label: t('language.ja'), flag: '🇯🇵' },
        { code: 'en', label: t('language.en'), flag: '🇺🇸' },
        { code: 'ko', label: t('language.ko'), flag: '🇰🇷' }
    ];

    const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

    const handleLanguageChange = (langCode) => {
        if (langCode === 'ko') {
            setShowKoreanDialog(true);
            setIsOpen(false);
        } else {
            i18n.changeLanguage(langCode);
            setIsOpen(false);
        }
    };

    const handleCloseKoreanDialog = () => {
        setShowKoreanDialog(false);
    };

    return (
        <>
            <LanguageSwitcherContainer inline={inline}>
                <LanguageButton inline={inline} onClick={() => setIsOpen(!isOpen)}>
                    <span>{currentLanguage.flag}</span>
                    <span>{currentLanguage.label}</span>
                </LanguageButton>
                
                {isOpen && (
                    <LanguageDropdown>
                        {languages.map((lang) => (
                            <LanguageOption
                                key={lang.code}
                                active={i18n.language === lang.code}
                                onClick={() => handleLanguageChange(lang.code)}
                            >
                                <span>{lang.flag}</span>
                                <span>{lang.label}</span>
                            </LanguageOption>
                        ))}
                    </LanguageDropdown>
                )}
            </LanguageSwitcherContainer>

            {showKoreanDialog && (
                <DialogOverlay onClick={handleCloseKoreanDialog}>
                    <Dialog onClick={(e) => e.stopPropagation()}>
                        <DialogTitle>{t('koreanDialog.title')}</DialogTitle>
                        <DialogMessage>{t('koreanDialog.message')}</DialogMessage>
                        <DialogButton onClick={handleCloseKoreanDialog}>
                            {t('koreanDialog.confirm')}
                        </DialogButton>
                    </Dialog>
                </DialogOverlay>
            )}
        </>
    );
};

export default LanguageSwitcher;

